export const Routes = {
    // Public/Auth routes (accessible to all)
    public: {
        login: '/login',
        authCallback: '/auth-callback',
        loginHome: '/login-home',
        register: '/register',
        userRegister: '/user-register',
        vendorRegister: '/vendor-register',
        skillcatRegister: '/skillcat-register',
        requestPasswordReset: '/request-password-reset',
        checkEmailConfirmation: '/check-email-confirmation',
        resetPassword: '/forgot-password',
        verify: '/verify',
        deleteAccount: '/delete-account',
        nationalPropertyList: '/national-property-list',
        nvl: {
            root: '/national-vendor-list',
            list: '/national-vendor-list/list',
        },
        vendorPricing: '/update-vendor-pricing/:link_token',
        bidRFP: '/bid-rfp/:link_token',
        rfpMessage: '/rfp-message/:link_token',
        guestCardForm: '/guest-card/:token',
        guestCardLink: '/guest-card-link/:token',
        applicationGate: '/apply/:token',
        screeningMock: '/screening-mock/:token',
        vendorCOIUpload: '/vendor-coi-upload',
        vendorW9Upload: '/vendor-w9-upload',
    },

    // Apartment-specific routes
    apartment: {
        schedule: {
            root: '/schedule',
            detail: '/schedule/:id',
            archived: '/schedule/:id/archive',
        },
        dailyActivity: {
            root: '/daily-activity',
        },
        records: {
            root: '/records',
            people: '/records/people',
            unit: '/records/units/:unitId',
            person: '/records/people/:userId',
            lead: '/records/leads/:leadId',
        },
        inspection: {
            root: '/inspect',
            detail: '/inspect/:id',
            configureType: '/inspect/configure-type',
            configureTypeDetail: '/inspect/configure-type/:id',
        },
        jobBoard: '/job-board',
        actionItems: '/action-item',
        lease: {
            root: '/leases',
        },
        config: {
            inventory: '/inventory-config',
            area: '/area-config',
            unit: '/unit-config',
            damage: '/damage-config',
            statusGroups: '/status-groups',
            changeOrder: '/change-order-config',
            infrastructure: {
                manager: '/infrastructure-manager',
                config: '/infrastructure-configure',
            },
            propertyAutomation: '/property-automation',
            serviceCategories: '/service-categories',
            furnitureInstalls: '/furniture-installs',
        },
        rfp: {
            rfpDashboard: '/rfp-dashboard',
            rfpDetail: '/rfp-dashboard/:rfpId',
        },
        billing: {
            root: '/billing',
        },
        units: {
            root: '/units',
        },
        inventoryManagement: {
            root: '/inventory-management',
        },
    },

    // Vendor-specific routes
    vendor: {
        ezwork: '/ezwork',
        subsPricing: '/subs/pricing/:subUserId',
        subsPricingRoot: '/subs/pricing',
        subs: '/subs',
        settings: '/vendor-settings',
        help: '/vendor-help',
        analytics: '/analytics/:id/',
        projectAnalytics: '/vendor-project-analytics',
        nationalPropertyList: '/national-property-list',
    },

    // Shared features between Apartment and Vendor
    shared: {
        communication: {
            ezchat: '/ez-chat',
        },
        financial: {
            invoiceHome: '/invoice-home',
            invoiceList: '/invoice/company/:id',
        },
        analytics: {
            root: '/analytics',
        },
        settings: {
            root: '/settings',
            general: '/general-settings',
        },
        reports: '/reports',
        demoData: '/demo-data',
        workforce: '/workforce',
        help: '/help',
        propertyDetails: '/property-details',
        billing: '/billing',
        dailyActivity: '/daily-activity',
        records: '/records',
    },

    // Organization-specific routes
    organization: {
        root: '/organization',
        detail: '/organization/:id',
        jobBoard: '/organization-job-board',
        scheduleDetail: '/organization-schedule/:id',
        leaseAuditDetail: '/organization-lease-audit/:id',
        schedule: '/organization-schedule',
        properties: '/organization-properties',
        directory: '/organization-directory',
        facilities: '/organization-facilities',
        projects: '/organization-projects',
        workforce: '/organization-workforce',
        rfp: {
            rfpDashboard: '/organization-rfp',
            rfpDetail: '/organization-rfp/:rfpId',
        },
        propertyForms: '/organization-property-forms',
        propertyAudit: {
            root: '/organization-property-audit',
        },
        deadlines: '/ezfolio-deadlines-dashboard',
        contractManager: '/ezfolio-contract-manager',
        copyTool: '/ezfolio-copy-tool',
        configurations: '/ezfolio-configurations',
        damages: '/ezfolio-damages',
        invoiceHome: '/ezfolio-invoices',
        invoiceList: '/ezfolio-invoices/company/:id',
        moveInSurvey: '/ezfolio-move-in-survey',
    },

    // Admin-specific routes
    admin: {
        root: '/admin',
        organization: {
            list: '/admin/organization',
            detail: {
                root: '/admin/organization/:id',
                users: '/admin/organization/:id/users',
                settings: '/admin/organization/:id/settings',
                properties: '/admin/organization/:id/properties',
            },
        },
        users: {
            detail: '/admin/users/:id',
            root: '/admin/users',
        },
        properties: {
            detail: '/admin/properties/:id',
            root: '/admin/properties',
        },
        vendors: {
            detail: '/admin/vendors/:id',
            root: '/admin/vendors',
        },
        schools: {
            root: '/admin/schools',
        },
        invoices: {
            detail: '/admin/invoices/:id',
            root: '/admin/invoices',
        },
        analytics: '/organization-analytics',
        loriManager: '/lori-manager',
        supportTicket: '/support-tickets',
        kpi: '/kpi',
        forms: '/forms',
        reports: '/admin/reports',
        billingGroups: '/billing-groups',
        outreach: '/outreach',
        applicationAccess: '/admin/application-access',
        onboardApartment: '/admin/onboard-apartment',
        copyConfigs: '/admin/copy-configs',
        workspaceManager: '/admin/workspace-manager',
        bannerNotifications: '/admin/banner-notifications',
    },

    // Manage-specific routes
    manage: {
        root: '/manage',
        propertyEvents: {
            root: '/manage/property-events',
        },
        leasingSeason: {
            root: '/manage/leasing-season',
            seasons: '/manage/leasing-season/seasons',
            campaigns: {
                root: '/manage/leasing-season/campaigns',
                detail: '/manage/leasing-season/campaigns/:id',
                create: '/manage/leasing-season/create-campaign',
            },
        },
        signableDocs: {
            root: '/manage/signable-docs',
            new: '/manage/signable-docs/new',
            detail: '/manage/signable-docs/:id',
        },
        website: {
            root: '/manage/website',
        },
        lease: {
            root: '/manage/lease',
            audit: '/lease-audit',
            auditDetail: '/lease-audit/:id',
        },
        propertyAudit: {
            root: '/property-audit',
        },
        rent: {
            root: '/rent',
        },
        finance: {
            root: '/manage/finance',
        },
        prospects: {
            root: '/manage/prospects',
            upload: '/manage/prospects/upload/:schoolId',
        },
        phone: {
            root: '/manage/phone',
        },
        voiceAgent: {
            root: '/manage/voice-agent',
            setup: '/manage/voice-agent/setup',
            settings: '/manage/voice-agent/settings',
            calls: '/manage/voice-agent/calls',
            callDetail: '/manage/voice-agent/calls/:id',
        },
        campaign: {
            root: '/manage/campaign',
            detail: '/manage/campaign/:id',
            create: '/manage/create-campaign',
            guestCards: '/manage/guest-cards',
        },
        lead: {
            root: '/manage/lead',
        },
        webTools: '/manage/web-tools',
        contractManager: '/manage/contract-manager',
        moveOutManagement: '/manage/move-out-management',
        concessions: {
            root: '/manage/concessions',
        },
        tenantMessages: {
            root: '/manage/tenant-messages',
        },
        /**
         * Property leasing season config workspace (Setup + Compose).
         * Detail `:id` is the Leasing Season Config bundle (six artifact slots).
         */
        leasingSeasonConfig: {
            root: '/manage/leasing-season-config',
            detail: '/manage/leasing-season-config/:id',
        },
        /** Property-level third-party account connections (Stripe, Checkr) */
        integrations: '/manage/integrations',
    },

    // Error pages
    error: {
        permissionDenied: '/denied',
        applicationAccessDenied: '/application-access-denied',
    },
} as const
// Updated type to only include string paths
export type RoutePath = {
    [K in keyof typeof Routes]: typeof Routes[K] extends string
        ? typeof Routes[K]
        : never
}[keyof typeof Routes]

// Helper to ensure type safety when using routes
export const getRoutePath = (path: RoutePath): string => path
